import React, { useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Platform, Animated, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../store';
import { setSelectedKpi } from '../../../../store/slices/standupRedirectSlice';
import Svg, { Path } from 'react-native-svg';
import SparcKpiCard from '../SparcKpiCard';
import SparcKpiDetailModal from '../SparcKpiDetailModal';
import LighthouseKpiCard from '../LighthouseKpiCard';
import { StarIconOutline, PrintIcon } from '../StandupIcons';
import SparcKpiListPrint, { SparcKpiListPrintRef, SparcKpiPrintCard } from '../Print/SparcKpiListPrint';
import SparcKpiPrintOptionsModal from '../Print/SparcKpiPrintOptionsModal';
import FontFamily from '../../../../constants/fonts';
import { useSparcKpiFavorite } from '../../hooks/useSparcKpiFavorite';
import LHModalPop from '../../../../components/LHModalPop';
import { getUnfavouriteModalConfig } from '../../constants/unfavouriteModalConfig';
import { styles } from './SparcKpiListSection.styles';
import {
  KPI_CATEGORY_COLORS,
  KPI_CATEGORY_FILTER_ORDER,
  resolveKpiCategory,
} from '../../../AnalyticsHub/utils/kpiHelpers';

// Loading Skeleton Component
const SparcKpiCardSkeleton: React.FC = () => {
  const opacity = React.useRef(new Animated.Value(0.3)).current;

  React.useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={styles.skeletonCard}>
      {/* Header Skeleton */}
      <View style={styles.skeletonHeader}>
        <View style={styles.skeletonHeaderLeft}>
          <Animated.View style={[styles.skeletonBadge, { opacity }]} />
          <Animated.View style={[styles.skeletonTitle, { opacity }]} />
        </View>
        <Animated.View style={[styles.skeletonFavorite, { opacity }]} />
      </View>

      {/* Metrics Skeleton */}
      <View style={styles.skeletonMetrics}>
        {[1, 2, 3].map((_, index) => (
          <View key={index} style={styles.skeletonMetricItem}>
            <Animated.View style={[styles.skeletonMetricLabel, { opacity }]} />
            <Animated.View style={[styles.skeletonMetricValue, { opacity }]} />
          </View>
        ))}
      </View>

      {/* Chart Skeleton */}
      <View style={styles.skeletonChart}>
        <View style={styles.skeletonYAxis}>
          <Animated.View style={[styles.skeletonYLabel, { opacity }]} />
          <Animated.View style={[styles.skeletonYLabel, { opacity }]} />
        </View>
        <View style={styles.skeletonBars}>
          {[1, 2, 3, 4, 5, 6, 7].map((_, index) => (
            <Animated.View
              key={index}
              style={[
                styles.skeletonBar,
                { height: 20 + Math.random() * 40, opacity },
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

interface SparcKpiListSectionProps {
  favoriteCards: string[];
  allFavoriteCards?: string[];
  onToggleFavorite: (cardId: string) => void;
  onToggleLighthouseFavorite?: (cardId: string) => void;
  onCreateActionPlan?: (card: any) => void;
  selectedPeriod: string;
  onPeriodChange?: (period: string) => void;
  lighthouseCardsData?: Array<{
    id: string;
    title: string;
    cw: number;
    lcw: number;
    lcp: number;
    ytd: number;
    chartData: Array<{ date: string; value: number }>;
  }>;
  sparcCardsData?: Array<{
    id: string;
    title: string;
    category?: string;
    badgeLetter: string;
    badgeColor: string;
    actual: number | null;
    target: number | null;
    delta: number | null;
    invertDeltaColor?: boolean;
    isMandatory?: boolean;
    chartData: Array<{ date: string; value: number | null }>;
  }>;
  isLoading?: boolean;
}

export const SparcKpiListSection: React.FC<SparcKpiListSectionProps> = ({ 
  favoriteCards, 
  allFavoriteCards, 
  onToggleFavorite, 
  onToggleLighthouseFavorite,
  onCreateActionPlan,
  selectedPeriod, 
  onPeriodChange,
  lighthouseCardsData = [],
  sparcCardsData = [],
  isLoading = false,
}) => {
  const dispatch = useDispatch();
  const sparcKpiPrintRef = useRef<SparcKpiListPrintRef | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showPrintOptionsModal, setShowPrintOptionsModal] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';

  // Unfavourite confirmation modal state
  const [showUnfavouriteModal, setShowUnfavouriteModal] = useState(false);
  const [pendingUnfavouriteCardId, setPendingUnfavouriteCardId] = useState<string | null>(null);
  const [pendingUnfavouriteCardTitle, setPendingUnfavouriteCardTitle] = useState<string | null>(null);
  const [pendingUnfavouriteType, setPendingUnfavouriteType] = useState<'sparc' | 'lighthouse' | null>(null);

  // Get hooks for API calls and modal handlers
  const { 
    toggleFavorite: toggleSparcFavorite,
    isSparcKpiFavorited,
    isLighthouseKpiFavorited,
    handleSparcToggleFavorite,
    handleLighthouseToggleFavorite,
    handleConfirmUnfavourite,
    handleCancelUnfavourite,
  } = useSparcKpiFavorite({
    setShowUnfavouriteModal,
    setPendingUnfavouriteCardId,
    setPendingUnfavouriteCardTitle,
    setPendingUnfavouriteType,
    pendingUnfavouriteCardId,
    pendingUnfavouriteCardTitle,
    pendingUnfavouriteType,
    sparcCards: sparcCardsData,
    lighthouseCardsData,
    onToggleFavorite,
    onToggleLighthouseFavorite,
  });

  const GAP = 12; // space between cards (px)

  // Calculate responsive card width based on screen size - matching Lighthouse Execution style
  // Start with 4 cards per row, then increase to 5, 6+ as screen gets wider
  const getCardWidth = () => {
    if (windowWidth >= 1600) return 'calc(20% - 10px)'; // 5 columns for very wide
    if (windowWidth >= 1900) return 'calc(16.666% - 10px)'; // 6 columns for ultra-wide
    if (windowWidth >= 1400) return 'calc(25% - 9px)'; // 4 columns (default wide)
    if (windowWidth >= 1024) return 'calc(33.333% - 8px)'; // 3 columns
    if (windowWidth >= 768) return 'calc(50% - 6px)'; // 2 columns
    return '100%'; // 1 column for mobile
  };

  const getMinWidth = () => {
    if (windowWidth >= 1400) return 250;
    if (windowWidth >= 768) return 0;
    return 0;
  };

  // Get favorites from Redux
  const favoritesFromRedux = useSelector(
    (state: RootState) => state.standupRedirect.favorites.data
  );

  // Use dynamic sparcCardsData from props, fallback to empty array if loading or no data
  const sparcCards = sparcCardsData;

  const sparcGroupedCards = React.useMemo(() => {
    return KPI_CATEGORY_FILTER_ORDER
      .map((category) => ({
        category,
        cards: sparcCards.filter((card) => {
          const resolvedCategory = resolveKpiCategory(card.category || card.badgeLetter || '');
          return resolvedCategory === category;
        }),
      }))
      .filter((group) => group.cards.length > 0);
  }, [sparcCards]);

  // Build combined favorite cards list from Redux favorites data
  const allSelectedCards = React.useMemo(() => {
    // Extract the actual favorites array from the nested structure
    // favoritesFromRedux has structure: { success: true, data: [...], meta: {...} }
    const favoritesArray = Array.isArray(favoritesFromRedux) 
      ? favoritesFromRedux 
      : (favoritesFromRedux as any)?.data || [];

    console.log('Favorites from Redux:', favoritesArray);

    const selectedCardsFromFavorites = favoritesArray
      .filter((fav: any) => fav.is_favourite === true)
      .map((favorite: any) => {
        const itemType = favorite.item_type;
        const itemId = favorite.item_id;
        const itemName = favorite.item_name;

        if (itemType === 'kpi') {
          // Stable-id match: item_id is the canonical kpi_code for SPARC KPIs.
          const sparcCard = sparcCards.find(card => String(card.id) === String(itemId));
          return sparcCard ? { type: 'sparc', data: sparcCard } : null;
        } else if (itemType === 'lighthouse_metric') {
          // Match Lighthouse cards by title (item_name) since item_id is stored as "pulse_check" format
          const lighthouseCard = lighthouseCardsData.find(card => card.title === itemName);
          return lighthouseCard ? { type: 'lighthouse', data: lighthouseCard } : null;
        }
        return null;
      })
      .filter((item: any): item is { type: string; data: any } => item !== null);

    const selectedMandatorySparcCards = sparcCards
      .filter((card: any) => card.isMandatory)
      .map((card: any) => ({ type: 'sparc', data: card }));

    const dedupedSelectedCardsMap = new Map<string, { type: string; data: any }>();
    [...selectedCardsFromFavorites, ...selectedMandatorySparcCards].forEach((item: any) => {
      const uniqueKey = `${item.type}-${item.data.id || item.data.title}`;
      if (!dedupedSelectedCardsMap.has(uniqueKey)) {
        dedupedSelectedCardsMap.set(uniqueKey, item);
      }
    });

    return Array.from(dedupedSelectedCardsMap.values());
  }, [favoritesFromRedux, sparcCards, lighthouseCardsData]);

  const selectedGroupedCards = React.useMemo(() => {
    const sparcGroups = KPI_CATEGORY_FILTER_ORDER
      .map((category) => ({
        key: category,
        label: category.toUpperCase(),
        color: KPI_CATEGORY_COLORS[category].icon,
        items: allSelectedCards.filter((item: any) => {
          if (item.type !== 'sparc') return false;
          const resolvedCategory = resolveKpiCategory(item.data.category || item.data.badgeLetter || '');
          return resolvedCategory === category;
        }),
      }))
      .filter((group) => group.items.length > 0);

    const lighthouseItems = allSelectedCards.filter((item: any) => item.type === 'lighthouse');
    const lighthouseGroup = lighthouseItems.length > 0
      ? [{
          key: 'lighthouse-execution',
          label: 'LIGHTHOUSE EXECUTION',
          color: '#0B5FC6',
          items: lighthouseItems,
        }]
      : [];

    return [...sparcGroups, ...lighthouseGroup];
  }, [allSelectedCards]);

  // Print should include the entire SPARC KPI List section with isFavorite status attached
  const sparcCardsToPrint = (sparcCards as SparcKpiPrintCard[]).map((card) => {
    const isFav = allSelectedCards.some((item: any) => item.type === 'sparc' && item.data.id === card.id);
    return {
      ...card,
      isFavorite: isFav,
    };
  });

  // Dispatch selected cards to Redux whenever they change
  React.useEffect(() => {
    // Convert the selected cards to a flat array of card data for Redux
    const selectedKpiData = allSelectedCards.map((item: any) => ({
      ...item.data,
      type: item.type,
    }));
    dispatch(setSelectedKpi(selectedKpiData));
  }, [allSelectedCards, dispatch]);

  // Handle card click to show modal
  const handleCardClick = (card: any) => {
    setSelectedCard(card);
    setIsModalVisible(true);
  };

  // Handle modal dismiss
  const handleModalDismiss = () => {
    setIsModalVisible(false);
    setSelectedCard(null);
  };

  const handleCreateActionPlan = () => {
    // Handle action plan creation - call parent callback with selected card
    if (onCreateActionPlan && selectedCard) {
      onCreateActionPlan(selectedCard);
    }
    setIsModalVisible(false);
  };

  const modalConfig = getUnfavouriteModalConfig('review-performance');

  return (
    <View style={styles.sparcKpiListSection}>
      <SparcKpiListPrint ref={sparcKpiPrintRef} sparcCards={sparcCardsToPrint} frequencyLabel={selectedPeriod} />
      <SparcKpiPrintOptionsModal
        visible={showPrintOptionsModal}
        onClose={() => setShowPrintOptionsModal(false)}
        onPrintAllKpis={() => {
          setShowPrintOptionsModal(false);
          sparcKpiPrintRef.current?.print();
        }}
        onPrintOnePerPage={() => {
          setShowPrintOptionsModal(false);
          sparcKpiPrintRef.current?.printOnePerPage();
        }}
      />
      {/* Unfavourite Confirmation Modal */}
      <LHModalPop
        visible={showUnfavouriteModal}
        onClose={handleCancelUnfavourite}
        title={modalConfig.header}
        showFooter={true}
        primaryButtonText={modalConfig.primaryButton}
        secondaryButtonText={modalConfig.secondaryButton}
        onPrimaryPress={handleConfirmUnfavourite}
        onSecondaryPress={handleCancelUnfavourite}
        showCloseButton={true}
      >
        <View style={{ gap: 12, paddingVertical: 8 }}>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {modalConfig.bodyLine1}
          </Text>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {modalConfig.bodyLine2}
          </Text>
        </View>
      </LHModalPop>

      {/* Main Header with Print Button */}
      <View style={styles.sparcKpiListMainHeader}>
        <Text style={styles.sparcKpiListMainTitle}>Select KPI's</Text>
        {isWeb && (
          <TouchableOpacity
            style={styles.printButtonContainer}
            onPress={() => setShowPrintOptionsModal(true)}
          >
            <PrintIcon />
            <Text style={styles.printButtonText}>Print</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Board Container */}
      <View style={styles.sparcBoardContainer}>
        {/* SPARCH Selected Divider */}
        <View style={styles.dividerWithHeader}>
          <Text style={styles.sparcBoardTitle}>SELECTED KPI'S</Text>
          <View style={styles.dividerLineCenter} />
        </View>
        {/* SPARC Selected Section - Conditionally render card or selected cards */}
        {isLoading ? (
          // Loading State - Show skeletons
          <View style={styles.sparcSelectedCardsRow}>
            {[1, 2, 3, 4].map((index) => (
              <View 
                key={`skeleton-selected-${index}`} 
                style={[
                  styles.sparcSelectedCardWrapper,
                  { 
                    flexBasis: getCardWidth() as any,
                    maxWidth: getCardWidth() as any,
                    minWidth: getMinWidth(),
                  }
                ]}
              >
                <SparcKpiCardSkeleton />
              </View>
            ))}
          </View>
        ) : allSelectedCards.length === 0 ? (
          // Empty State Card
          <View style={styles.sparcSelectedSection}>
            <View style={styles.emptyStateContainer}>
              <StarIconOutline />
              <Text style={styles.emptyStateTitle}>Select a SPARC KPI or Lighthouse Execution</Text>
              <Text style={styles.emptyStateDescription}>
                Start tracking your team progress from the cards below
              </Text>
            </View>
          </View>
        ) : (
          // Selected Cards grouped by SPARC categories + Lighthouse Execution
          <>
            {selectedGroupedCards.map((group) => (
              <View key={`selected-group-${group.key}`} style={styles.sparcGroupContainer}>
                <View style={styles.sparcGroupHeader}>
                  <View style={[styles.sparcGroupDot, { backgroundColor: group.color }]} />
                  <Text style={styles.sparcGroupTitle}>{group.label}</Text>
                  <Text style={styles.sparcGroupBadge}>
                    {group.items.length} {group.items.length === 1 ? 'metric' : 'metrics'}
                  </Text>
                </View>

                <View style={styles.sparcSelectedCardsRow}>
                  {group.items.map((item: any) => (
                    <View
                      key={`favorite-${group.key}-${item.data.id}`}
                      style={[
                        styles.sparcSelectedCardWrapper,
                        {
                          flexBasis: getCardWidth() as any,
                          maxWidth: getCardWidth() as any,
                          minWidth: getMinWidth(),
                        }
                      ]}
                    >
                      {item.type === 'sparc' ? (
                        <SparcKpiCard
                          id={item.data.id}
                          title={item.data.title}
                          badgeLetter={item.data.badgeLetter}
                          badgeColor={item.data.badgeColor}
                          actual={item.data.actual}
                          target={item.data.target}
                          delta={item.data.delta}
                          invertDeltaColor={item.data.invertDeltaColor}
                          isMandatory={Boolean(item.data.isMandatory)}
                          chartData={item.data.chartData}
                          isFavorite={true}
                          onFavoritePress={item.data.isMandatory ? undefined : () => handleSparcToggleFavorite(item.data.id)}
                          disableFavorite={Boolean(item.data.isMandatory)}
                          disableFavTooltip={true}
                          disableApiCall={true}
                        />
                      ) : (
                        <LighthouseKpiCard
                          title={item.data.title}
                          cw={item.data.cw}
                          lcw={item.data.lcw}
                          lcp={item.data.lcp}
                          ytd={item.data.ytd}
                          chartData={item.data.chartData}
                          isFavorite={true}
                          onFavoritePress={() => handleLighthouseToggleFavorite(item.data.id)}
                          disableExpandTooltip={true}
                          disableFavTooltip={true}
                          disableApiCall={true}
                        />
                      )}
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </>
        )}

        {/* Divider with Header and Dropdown */}
        <View style={styles.dividerWithHeader}>
          <Text style={styles.sparcBoardTitle}>SPARC KPI LIST</Text>
          <View style={styles.dividerLineCenter} />
          <View style={styles.dropdownContainer}>
            <TouchableOpacity 
              style={styles.dropdown}
              onPress={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <View style={styles.dropdownTextContainer}>
                <Text style={styles.dropdownText}>{selectedPeriod}</Text>
              </View>
              <View style={styles.dropdownDivider} />
              <View style={styles.dropdownIconContainer}>
                <Svg width="8" height="5" viewBox="0 0 8 5" fill="none">
                  <Path d="M0.94 0L4 3.05333L7.06 0L8 0.94L4 4.94L0 0.94L0.94 0Z" fill="#004895" />
                </Svg>
              </View>
            </TouchableOpacity>
            {isDropdownOpen && (
              <View style={styles.dropdownMenu}>
                {['Daily', 'Weekly', 'Period'].map((period) => (
                  <TouchableOpacity
                    key={period}
                    style={styles.dropdownMenuItem}
                    onPress={() => {
                      onPeriodChange?.(period);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <Text style={[
                      styles.dropdownMenuItemText,
                      selectedPeriod === period && styles.dropdownMenuItemTextActive
                    ]}>
                      {period}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        </View>

        {/* SPARC Cards Row (grouped by category like Analytics Hub) */}
        {isLoading ? (
          <View style={styles.sparcCardsRow}>
            {[1, 2, 3, 4].map((index) => (
              <View 
                key={`skeleton-${index}`} 
                style={[
                  styles.sparcCardEqualWrapper,
                  { 
                    flexBasis: getCardWidth() as any,
                    maxWidth: getCardWidth() as any,
                    minWidth: getMinWidth(),
                  }
                ]}
              >
                <SparcKpiCardSkeleton />
              </View>
            ))}
          </View>
        ) : sparcCards.length === 0 ? (
          <Text style={styles.emptyText}>No KPI metrics available</Text>
        ) : (
          sparcGroupedCards.map((group) => {
            const colors = KPI_CATEGORY_COLORS[group.category];
            const metricCount = group.cards.length;

            return (
              <View key={group.category} style={styles.sparcGroupContainer}>
                <View style={styles.sparcGroupHeader}>
                  <View style={[styles.sparcGroupDot, { backgroundColor: colors.icon }]} />
                  <Text style={styles.sparcGroupTitle}>{group.category.toUpperCase()}</Text>
                  <Text style={styles.sparcGroupBadge}>
                    {metricCount} {metricCount === 1 ? 'metric' : 'metrics'}
                  </Text>
                </View>

                <View style={styles.sparcCardsRow}>
                  {group.cards.map((card) => (
                    <View 
                      key={`${group.category}-${card.id}`}
                      style={[
                        styles.sparcCardEqualWrapper,
                        {
                          flexBasis: getCardWidth() as any,
                          maxWidth: getCardWidth() as any,
                          minWidth: getMinWidth(),
                        }
                      ]}
                    >
                      <SparcKpiCard
                        id={card.id}
                        title={card.title}
                        badgeLetter={card.badgeLetter}
                        badgeColor={card.badgeColor}
                        actual={card.actual}
                        target={card.target}
                        delta={card.delta}
                        invertDeltaColor={card.invertDeltaColor}
                        isMandatory={Boolean(card.isMandatory)}
                        chartData={card.chartData}
                        isFavorite={Boolean(card.isMandatory) || isSparcKpiFavorited(card.id)}
                        onFavoritePress={card.isMandatory ? undefined : () => handleSparcToggleFavorite(card.id)}
                        disableFavorite={Boolean(card.isMandatory)}
                        disableApiCall={Boolean(card.isMandatory) || isSparcKpiFavorited(card.id)}
                        onPress={() => handleCardClick(card)}
                      />
                    </View>
                  ))}
                </View>
              </View>
            );
          })
        )}
      </View>

      {/* SPARC KPI Detail Modal */}
      <SparcKpiDetailModal
        visible={isModalVisible}
        onDismiss={handleModalDismiss}
        onCreateActionPlan={handleCreateActionPlan}
        cardData={selectedCard}
        isFavorite={selectedCard ? (Boolean(selectedCard.isMandatory) || isSparcKpiFavorited(selectedCard.id)) : false}
        isMandatory={Boolean(selectedCard?.isMandatory)}
        onToggleFavorite={() => selectedCard && !selectedCard.isMandatory && onToggleFavorite(selectedCard.id)}
      />
    </View>
  );
};

